import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { Tabs, TabsList, TabsTrigger } from '@parrot-software/design-system';
import type { SubscriptionBlockViewDto } from '@subscriptions/application/dtos';
import { ErrorState } from '@subscriptions/presentation/components/base';
import { useAreSubscriptionActionsLocked } from '@subscriptions/presentation/hooks/useAreSubscriptionActionsLocked';
import { BlockDetailContentSkeleton } from './BlockDetailSkeleton';
import { BlockInvoicingContent } from './BlockInvoicingContent';
import { BlockPaymentHistoryContent } from './BlockPaymentHistoryContent';
import { BlockSummaryContent } from './BlockSummaryContent';

const TAB_KEYS = ['summary', 'paymentHistory', 'invoicing'] as const;
type TabKey = typeof TAB_KEYS[number];

const isValidTab = (value: string | null): value is TabKey => TAB_KEYS.includes(value as TabKey);

interface BlockDetailTabsProps {
  block?: SubscriptionBlockViewDto;
  isError: boolean;
  isLoading: boolean;
  onRetry: () => void;
}

/**
 * BlockDetailTabs - Tabs wrapper for the block detail page
 *
 * Uses `?tab=` query parameter as the single source of truth.
 * Tabs: summary, paymentHistory, invoicing.
 * Renders loading skeleton, error state, or tab content depending on data state.
 * A read-only organization gets the invoicing tab disabled and lands on summary if the URL asks for it.
 *
 * @param {Object} props - Component properties
 * @param {SubscriptionBlockViewDto} [props.block] - Subscription block data (undefined while loading or on error)
 * @param {boolean} props.isError - Whether the data fetch failed
 * @param {boolean} props.isLoading - Whether the block is loading with nothing cached yet
 * @param {Function} props.onRetry - Callback to retry fetching data
 * @returns {React.ReactElement} Tabs component
 *
 * @example
 * <BlockDetailTabs block={block} isError={isError} isLoading={isLoading} onRetry={refetch} />
 */
export const BlockDetailTabs = ({ block, isError, isLoading, onRetry }: BlockDetailTabsProps) => {
  const { t } = useTranslation('subscriptions');
  const history = useHistory();
  const { search, pathname } = useLocation();

  const areActionsLocked = useAreSubscriptionActionsLocked();
  const isTabDisabled = (tab: TabKey) => tab === 'invoicing' && areActionsLocked;
  const isTabAvailable = (value: string | null): value is TabKey => isValidTab(value) && !isTabDisabled(value);

  const tabParam = new URLSearchParams(search).get('tab');
  const isTabValid = isTabAvailable(tabParam);
  const activeTab: TabKey = isTabValid ? tabParam : TAB_KEYS[0];

  useEffect(() => {
    if (tabParam && !isTabValid) {
      const params = new URLSearchParams(search);
      params.delete('tab');
      const qs = params.toString();
      history.replace({ pathname, search: qs ? `?${qs}` : '' });
    }
  }, [tabParam, isTabValid, search, pathname, history]);

  const handleTabChange = (value: string) => {
    const params = new URLSearchParams(search);
    if (value === TAB_KEYS[0]) {
      params.delete('tab');
    } else {
      params.set('tab', value);
    }
    const qs = params.toString();
    history.replace({ pathname, search: qs ? `?${qs}` : '' });
  };

  const renderTabContent = () => {
    if (isLoading) {
      return <BlockDetailContentSkeleton />;
    }

    if (isError || !block) {
      return (
        <ErrorState
          onRetry={onRetry}
          retryLabel={t('blockDetail.error.retry')}
          title={t('blockDetail.error.description')}
        />
      );
    }

    if (activeTab === 'summary') {
      return <BlockSummaryContent block={block} />;
    }

    if (activeTab === 'paymentHistory') {
      return <BlockPaymentHistoryContent block={block} />;
    }

    return <BlockInvoicingContent block={block} />;
  };

  return (
    <div className="px-6 pt-2 pb-6">
      <div className="flex flex-col gap-4">
        <Tabs onValueChange={handleTabChange} value={activeTab}>
          <TabsList>
            {TAB_KEYS.map((key) => (
              <TabsTrigger disabled={isTabDisabled(key)} key={key} value={key}>
                {t(`blockDetail.tabs.${key}`)}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        {renderTabContent()}
      </div>
    </div>
  );
};
